/**
 * Umsatzsteuer — kein Ersatz fuer die Buchhaltung, sondern der Zeitversatz.
 *
 * Das ist Firmensache und keine persoenliche Einstellung: ob die Firma unter
 * §19 faellt und wann die Zahllast fliesst, gilt fuer das Haus, nicht fuer den
 * Einzelnen.
 */

import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button, Checkbox, toast } from '@elbklar/ui';

import { SelectField } from '@/components/Felder';
import { settingsApi } from '@/lib/api';
import { ganzzahl, hatFehler, ohne, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { FinanceSettings } from '@/types';

export function UmsatzsteuerForm({ einstellungen }: { einstellungen: FinanceSettings }) {
  const queryClient = useQueryClient();

  const [aktiv, setAktiv] = useState(false);
  const [zeitraum, setZeitraum] = useState('1');
  const [versatz, setVersatz] = useState('1');
  const [fehler, setFehler] = useState<Fehler<'zeitraum' | 'versatz'>>({});

  useEffect(() => {
    setAktiv(einstellungen.vat_enabled);
    setZeitraum(String(einstellungen.vat_period_months));
    setVersatz(String(einstellungen.vat_delay_months));
    setFehler({});
  }, [einstellungen]);

  const speichern = useMutation({
    mutationFn: () =>
      settingsApi.update({
        vat_enabled: aktiv,
        vat_period_months: Number(zeitraum),
        vat_delay_months: Number(versatz),
      }),
    onSuccess: () => {
      toast.success('Umsatzsteuer-Einstellungen gespeichert');
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['projection'] });
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  return (
    <div className="max-w-2xl">
      <p className="mb-4 text-muted-foreground">
        Kein Ersatz für die Buchhaltung. Es geht allein um den Zeitversatz: die
        Vorsteuer einer Anschaffung fließt heute ab und kommt erst mit der
        Voranmeldung zurück — ohne das ist die Projektion bei Ausgaben zu
        pessimistisch und bei Einnahmen zu optimistisch.
      </p>

      <form noValidate
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          // Grenzen aus FinanceSettingsUpdate (finance-api): Zeitraum 1 oder 3
          // Monate (UStG), Versatz 0–6 Monate. Die Auswahl bietet nur Gültiges
          // an; ein anders gespeicherter Wert fiele hier auf statt als 422.
          const neu = {
            zeitraum: zeitraum === '1' || zeitraum === '3' ? undefined : 'Monatlich oder quartalsweise wählen',
            versatz: ganzzahl(versatz, { min: 0, max: 6, pflicht: true }),
          };
          setFehler(neu);
          if (hatFehler(neu)) {
            zumErstenFehler(e.currentTarget);
            return;
          }
          speichern.mutate();
        }}
        className="space-y-4"
      >
        <Checkbox
          checked={aktiv}
          onChange={setAktiv}
          label={
            <span>
              Zahllast in der Projektion berücksichtigen
              <span className="block t-small text-muted-foreground">
                Aus lassen bei Kleinunternehmerregelung nach § 19 UStG.
              </span>
            </span>
          }
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Voranmeldezeitraum"
            options={[
              { value: '1', label: 'monatlich' },
              { value: '3', label: 'quartalsweise' },
            ]}
            disabled={!aktiv}
            value={zeitraum}
            onChange={(e) => {
              setZeitraum(e.target.value);
              setFehler((f) => ohne(f, 'zeitraum'));
            }}
            error={fehler.zeitraum}
          />
          <SelectField
            label="Zeitversatz"
            hint="Wann die Zahlung tatsächlich fließt"
            options={[
              { value: '1', label: '1 Monat — Regelfall' },
              { value: '2', label: '2 Monate — mit Dauerfristverlängerung' },
            ]}
            disabled={!aktiv}
            value={versatz}
            onChange={(e) => {
              setVersatz(e.target.value);
              setFehler((f) => ohne(f, 'versatz'));
            }}
            error={fehler.versatz}
          />
        </div>

        <p className="rounded-lg bg-muted px-3 py-2 t-small text-muted-foreground">
          Die Steuersätze stehen am einzelnen Posten — in den Buchungen, den
          Fixkosten und der Planung. Eingetragen wird immer der{' '}
          <strong className="font-medium text-foreground">Bruttobetrag</strong>; netto wird daraus gerechnet.
        </p>

        <Button type="submit" variant="primary" loading={speichern.isPending}>
          Einstellungen speichern
        </Button>
      </form>
    </div>
  );
}
